import React, { useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  alpha,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

import { useOnBoardingContext } from "./OnBoardingContext";
import { useStoreTagOptions } from "../../../hooks";
import { useUploadOrPickImages } from "../../../hooks/useUploadOrPickImages";
import { GfImage, GfMerchant } from "@swagger/typescript-fetch-goflyer";
import {
  StandardizedOnboardingData,
  validateOnboardingData,
} from "./utils/onboardingDataTransformer";
import {
  createFieldChangeHandler,
  FieldPath,
  FieldValue,
} from "./utils/fieldChangeHelpers";
import { StoreHeader } from "./components/StoreHeader";
import { OperatingHours } from "./components/OperatingHours";
import { StoreInformation } from "./components/StoreInformation";
import { LocationInformation } from "./components/LocationInformation";
import { ContactReviews } from "./components/ContactReviews";
import { GeographicCoordinates } from "./components/GeographicCoordinates";
import { createEmptyStoreData } from "./utils/createEmptyStoreData";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";

interface OnBoardingStep2Props {
  onContinue: (standardizedData: StandardizedOnboardingData) => void;
  onBack: () => void;
}

export const OnBoardingStep2: React.FC<OnBoardingStep2Props> = ({
  onContinue,
  onBack,
}) => {
  const { t } = useTranslation();
  const { onboardingData, setOnboardingData, selectedStore } =
    useOnBoardingContext();
  const { storeTagOptionList } = useStoreTagOptions();

  const [isEditing, setIsEditing] = useState(false);
  const [editedStore, setEditedStore] =
    useState<StandardizedOnboardingData | null>(
      onboardingData || createEmptyStoreData()
    );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const isSavingRef = useRef(false);

  const storeTagOptions = useMemo(() => {
    return (
      storeTagOptionList?.map((tag) => ({
        value: tag,
        label: tag.name,
      })) || []
    );
  }, [storeTagOptionList]);

  const fieldChangeHandler = useMemo(() => createFieldChangeHandler(), []);

  const validateCurrentData = useCallback(() => {
    if (!editedStore) {
      setValidationErrors([]);
      setValidationWarnings([]);
      return;
    }

    const { errors } = validateOnboardingData(editedStore);
    setValidationErrors(errors);
    setValidationWarnings([]);
  }, [editedStore]);

  const handleFieldChange = useCallback(
    (field: FieldPath, value: FieldValue) => {
      if (isSavingRef.current) return;
      setEditedStore(fieldChangeHandler(field, value));
    },
    [fieldChangeHandler]
  );

  const handleSaveChanges = () => {
    if (editedStore) {
      isSavingRef.current = true;
      setIsEditing(false);
      setOnboardingData(editedStore);
      setTimeout(() => {
        isSavingRef.current = false;
      }, 0);
    } else {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedStore(onboardingData);
    setIsEditing(false);
  };

  const handleContinue = () => {
    if (editedStore) {
      const { errors } = validateOnboardingData(editedStore);
      setValidationErrors(errors);
      setValidationWarnings([]);

      if (errors.length === 0) {
        onContinue(editedStore);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const { handleOnImageFileSelect, isUploadingImage } = useUploadOrPickImages({
    onPickedImageChange: (images: GfImage[]) => {
      const image = images[images.length - 1];
      setEditedStore((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          merchant: {
            ...prev.merchant,
            merchantLogo: image,
          } as GfMerchant,
        };
      });
    },
  });

  React.useEffect(() => {
    // If no onboarding data exists (manual entry), create empty data and enable editing
    if (!onboardingData && !selectedStore) {
      const emptyData = createEmptyStoreData();
      setEditedStore(emptyData);
      setIsEditing(true);
    } else if (onboardingData) {
      setEditedStore(onboardingData);
    }
  }, [onboardingData, selectedStore]);

  React.useEffect(() => {
    validateCurrentData();
  }, [editedStore]);

  if (!editedStore) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          {t(messages.Loading_Store_Details())}
        </Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          {t(messages.Go_Back())}
        </Button>
      </Box>
    );
  }

  const isManualEntry = !selectedStore && !onboardingData;

  const renderValidationAlerts = () => (
    <>
      {" "}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{t(messages.Please_Fix_Following_Issues())}</AlertTitle>
          <List dense>
            {validationErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemIcon>
                  <ErrorIcon color="error" />
                </ListItemIcon>
                <ListItemText primary={error} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
      {validationWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t(messages.Recommendations())}</AlertTitle>
          <List dense>
            {validationWarnings.map((warning, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemIcon>
                  <WarningIcon color="warning" />
                </ListItemIcon>
                <ListItemText primary={warning} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
    </>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <StoreHeader
        merchant={editedStore?.merchant}
        isEditing={isEditing || isManualEntry}
        isUploadingImage={isUploadingImage}
        onEdit={() => setIsEditing(true)}
        onSave={handleSaveChanges}
        onCancel={handleCancelEdit}
        onFieldChange={handleFieldChange}
        onImageFileSelect={handleOnImageFileSelect}
      />
      {(validationErrors.length > 0 || validationWarnings.length > 0) && (
        <Box sx={{ mb: 3 }}>{renderValidationAlerts()}</Box>
      )}{" "}
      {isManualEntry && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info">
            <AlertTitle>{t(messages.Manual_Entry_Mode())}</AlertTitle>
            {t(messages.Please_Fill_Required_Information())}{" "}
            {t(messages.All_Fields_Marked_Required())}
          </Alert>
        </Box>
      )}
      <Grid container spacing={3} sx={{ overflow: "visible" }}>
        {editedStore?.merchant && (
          <Grid item xs={12}>
            <StoreInformation
              merchant={editedStore?.merchant}
              store={editedStore?.store}
              isEditing={isEditing || isManualEntry}
              storeTagOptions={storeTagOptions}
              onFieldChange={handleFieldChange}
            />
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <LocationInformation
            store={editedStore?.store}
            isEditing={isEditing || isManualEntry}
            onFieldChange={handleFieldChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ContactReviews
            store={editedStore?.store}
            isEditing={isEditing || isManualEntry}
            onFieldChange={handleFieldChange}
          />
        </Grid>

        <Grid item xs={12}>
          <OperatingHours
            openingHours={editedStore?.store?.openingHours || []}
            isEditing={isEditing || isManualEntry}
            onFieldChange={handleFieldChange}
          />
        </Grid>

        <Grid item xs={12}>
          <GeographicCoordinates
            store={editedStore?.store}
            isEditing={isEditing || isManualEntry}
            onFieldChange={handleFieldChange}
          />
        </Grid>
      </Grid>
      <Box
        display="flex"
        justifyContent="space-between"
        mt={4}
        sx={{
          position: "sticky",
          bottom: 24,
          zIndex: 10,
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onBack}
          sx={{
            minWidth: { xs: 120, sm: 160 },
            height: 56,
            fontSize: "1rem",
            fontWeight: 600,
            borderRadius: 28,
            borderColor: "#02b7cf",
            color: "#02b7cf",
            "&:hover": {
              borderColor: "#0288a3",
              backgroundColor: alpha("#02b7cf", 0.05),
            },
          }}
        >
          {t(messages.Back())}
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={!editedStore || validationErrors.length > 0}
          sx={{
            minWidth: { xs: 200, sm: 240 },
            height: 56,
            fontSize: "1.125rem",
            fontWeight: 600,
            borderRadius: 28,
            backgroundColor: "#f58321",
            boxShadow: "0 8px 24px rgba(245, 131, 33, 0.4)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: "#e57100",
              transform: "translateY(-2px)",
              boxShadow: "0 12px 32px rgba(245, 131, 33, 0.5)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          {t(messages.Continue_To_Review())}
        </Button>
      </Box>
    </Box>
  );
};
